import { Component, inject } from "@angular/core";
import { RouterModule } from "@angular/router";
import {
  LucideAngularModule,
  X,
  Bot,
  MessagesSquare,
  Database,
  Sparkles,
  ArrowRight,
  Clock3,
} from "lucide-angular";
import { RecentItemsService } from "../../core/services/recent-items.service";
import { RecentItem } from "../../core/models/recent-items.model";

@Component({
  selector: "app-home-page",
  standalone: true,
  imports: [RouterModule, LucideAngularModule],
  templateUrl: "./home-page.component.html",
})
export class HomePageComponent {
  readonly X = X;
  readonly Bot = Bot;
  readonly MessagesSquare = MessagesSquare;
  readonly Database = Database;
  readonly Sparkles = Sparkles;
  readonly ArrowRight = ArrowRight;
  readonly Clock3 = Clock3;

  private readonly initialVisibleItems = 3;
  private readonly maxVisibleItems = 10;
  private readonly recentItemsService = inject(RecentItemsService);

  showAllRecentItems = false;

  get recentItems(): RecentItem[] {
    return this.recentItemsService.getAllRecentItems();
  }

  get cappedRecentItems(): RecentItem[] {
    return this.recentItems.slice(0, this.maxVisibleItems);
  }

  get visibleRecentItems(): RecentItem[] {
    const limit = this.showAllRecentItems
      ? this.maxVisibleItems
      : this.initialVisibleItems;
    return this.cappedRecentItems.slice(0, limit);
  }

  get hasMoreRecentItems(): boolean {
    return this.cappedRecentItems.length > this.initialVisibleItems;
  }

  get visibleRecentItemsCount(): number {
    return this.cappedRecentItems.length;
  }

  get recentKnowledgeBases() {
    return this.recentItemsService.getRecentKnowledgeBases();
  }

  get totalRecentCount(): number {
    return this.recentItems.length;
  }

  getItemInitial(name: string): string {
    return name.charAt(0).toUpperCase();
  }

  getItemTitle(item: RecentItem): string {
    switch (item.type) {
      case "chatbot":
        return item.name;
      case "conversation":
        return item.title;
      case "test":
        return item.name;
      case "user":
        return item.name;
      case "knowledge-base":
        return item.name;
    }
  }

  getItemSubtitle(item: RecentItem): string {
    switch (item.type) {
      case "chatbot":
        return item.code;
      case "conversation":
        return item.chatbotCode;
      case "test":
        return "Prueba automatizada";
      case "user":
        return item.email;
      case "knowledge-base":
        return item.code;
    }
  }

  getItemTypeLabel(item: RecentItem): string {
    switch (item.type) {
      case "chatbot":
        return "Chatbot";
      case "conversation":
        return "Conversacion";
      case "test":
        return "Prueba";
      case "user":
        return "Usuario";
      case "knowledge-base":
        return "Base de conocimiento";
    }
  }

  getItemIcon(item: RecentItem) {
    switch (item.type) {
      case "chatbot":
        return this.Bot;
      case "conversation":
        return this.MessagesSquare;
      case "test":
        return this.Sparkles;
      case "user":
        return this.Sparkles;
      case "knowledge-base":
        return this.Database;
    }
  }

  getItemSurfaceClass(item: RecentItem): string {
    switch (item.type) {
      case "chatbot":
        return "from-violet-500 to-fuchsia-600";
      case "conversation":
        return "from-sky-500 to-cyan-600";
      case "test":
        return "from-amber-500 to-orange-600";
      case "user":
        return "from-slate-500 to-slate-700";
      case "knowledge-base":
        return "from-emerald-500 to-teal-600";
    }
  }

  getRelativeTime(timestamp: number): string {
    const diffMs = Date.now() - timestamp;
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Justo ahora";
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours} h`;
    if (days < 7) return `Hace ${days} d`;

    return new Date(timestamp).toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  toggleRecentItems(): void {
    this.showAllRecentItems = !this.showAllRecentItems;
  }

  deleteRecentItem(event: Event, item: RecentItem): void {
    event.preventDefault();
    event.stopPropagation();
    this.recentItemsService.deleteRecentItem(item);
  }
}
